import type { ReactNode } from 'react';

/**
 * Um item da leitura: rótulo curto e valor.
 *
 * `tom` só existe para dinheiro e estado — verde é ganho, vermelho é perda,
 * "quiet" é informação de apoio (hora da última varredura, por exemplo). Cor
 * aqui informa; não decora.
 */
export type Leitura = {
  label: string;
  value: ReactNode;
  tom?: 'gain' | 'loss' | 'quiet';
};

type Props = {
  /** sobrelinha em caixa alta — marca a seção, não rotula campo */
  eyebrow?: string;
  title: string;
  /** uma frase, quando a tela precisa de contexto que o número não dá */
  description?: ReactNode;
  actions?: ReactNode;
  /**
   * A leitura da página: a linha de números que responde "como está isto"
   * antes de qualquer texto ser lido. Itens falsos são descartados, então a
   * chamada pode usar `cond && {...}` sem tratar undefined.
   */
  readout?: Array<Leitura | false | null | undefined | 0 | ''>;
};

const TOM: Record<NonNullable<Leitura['tom']>, string> = {
  gain: ' is-gain',
  loss: ' is-loss',
  quiet: ' is-quiet',
};

export function PageHead({ eyebrow, title, description, actions, readout }: Props) {
  const itens = (readout ?? []).filter((x): x is Leitura => Boolean(x));

  return (
    <header className="page-head">
      <div className="page-head-top">
        <div className="page-head-titles">
          {eyebrow && <div className="eyebrow">{eyebrow}</div>}
          <h1>{title}</h1>
        </div>
        {actions && <div className="page-actions">{actions}</div>}
      </div>

      {description && <p>{description}</p>}

      {itens.length > 0 && (
        <div className="readout">
          {itens.map((i) => (
            <div className="readout-item" key={i.label}>
              <span className={`readout-value${i.tom ? TOM[i.tom] : ''}`}>{i.value}</span>
              <span className="readout-label">{i.label}</span>
            </div>
          ))}
        </div>
      )}
    </header>
  );
}
